import { createSelector, MemoizedSelector } from '@ngrx/store';

import { Category } from '@fcom/dapi/api/models/category';
import { FinnairLowestPrice } from '@fcom/dapi/api/models/finnair-lowest-price';
import { FinnairServiceVariantItemV3 } from '@fcom/dapi/api/models/finnair-service-variant-item-v-3';
import { SubCategory } from '@fcom/dapi/api/models/sub-category';
import { mapValues } from '@fcom/core/utils/utils';
import {
  ServiceCatalogService,
  ServiceCatalogServices,
  ServiceSelections,
  TravelerService,
} from '@fcom/dapi/interfaces/services.interface';

import { asSegmentId, getLowestPrice } from '../../utils/common-booking.utils';
import { BUNDLE_CATEGORY_ANCILLARY_MAP } from '../../modules/ancillaries/utils/bundles.utils';
import {
  getBundleVariantCodes,
  getRelatedCategoriesForCategory,
  isBundle,
  isSeatBundle,
  SEAT_BUNDLE_CATEGORIES,
} from '../../modules/ancillaries/utils/category.utils';
import { selectCommonBookingState } from './feature.selector';
import { CommonBookingFeatureState, CommonBookingState } from '../store.interface';
import { seatBundleCatalogByFlightSelector, serviceCategoriesMapSelector } from './service-catalog.selector';

export interface BundleForFragment {
  constituentVariants: string[];
  constituents: ServiceCatalogService[];
  bundleItemByPax: Record<string, ServiceCatalogService>;
}
export type BundlesByCategory = Partial<Record<Category, Record<string, BundleForFragment>>>;

const resolveConstituentService = (
  constituentServices: ServiceCatalogServices | undefined,
  flightId: string,
  variantCodes: string[]
): ServiceCatalogService | null => {
  // Flight-based categories are keyed by flight id; bound-based ones by the single-segment bound id.
  const servicesByPax = constituentServices?.[flightId] ?? constituentServices?.[`BND-${flightId}`];
  if (!servicesByPax) {
    return null;
  }
  const allServices = Object.values(servicesByPax).flat();
  return allServices.find((service) => variantCodes.includes(service.variant)) ?? allServices[0] ?? null;
};

/**
 * Allows finding out which subcategories belong to a bundle category.
 * Include all categories that appear under seat maps for any of the flights on the journey.
 */
export const seatBundleCategoriesToSubCategoriesMappingSelector = createSelector(
  selectCommonBookingState,
  (state: CommonBookingState) => {
    const catalogItems = Object.values(state?.seatMap?.seatmaps || {}).flatMap(
      (f) => f?.seatMap?.bundleServiceCatalogItems || []
    );
    return catalogItems?.reduce((acc: Partial<Record<Category, Set<SubCategory>>>, cur) => {
      const curSubCategories = Object.values(cur.services || {})
        .flatMap((byFlight) => Object.values(byFlight))
        .flat(1)
        .flatMap((s) => s.subCategory);
      return {
        ...acc,
        [cur.category]: new Set<SubCategory>([...(acc[cur.category] || []), ...curSubCategories]),
      };
    }, {});
  }
);

/**
 * Allows finding out which category a subcategory belongs to.
 * Include all categories that appear under seat maps for any of the flights on the journey.
 */
export const seatBundleSubCategoriesToCategoriesMappingSelector = createSelector(
  seatBundleCategoriesToSubCategoriesMappingSelector,
  (catsToSubcats) =>
    Object.fromEntries(
      Object.entries(catsToSubcats).flatMap(([cat, subCats]) => Array.from(subCats).map((sc) => [sc, cat]))
    )
);

/**
 * Allows determining if a cateogry is a seat bundle or iterating through seat bundle category types.
 */
export const seatBundleCategoriesSelector = createSelector(
  seatBundleCategoriesToSubCategoriesMappingSelector,
  (catsToSubCats) => new Set(Object.keys(catsToSubCats) as Category[])
);

/**
 * Allows determining if a subcategory is a seat bundle or iterating through seat bundle category types.
 */
export const seatBundleSubCategoriesSelector: MemoizedSelector<CommonBookingState, Set<SubCategory>> = createSelector(
  seatBundleSubCategoriesToCategoriesMappingSelector,
  (subCatsToCats) => new Set(Object.keys(subCatsToCats) as SubCategory[])
);

/**
 * Returns seat bundle catalog service variant items for all flights and passengers for which there are any offered.
 */
export const seatBundleCatalogServicesByFlightAndPassengerSelector = createSelector(
  seatBundleCatalogByFlightSelector,
  (catalogByFlight): Record<string, Record<string, FinnairServiceVariantItemV3[]>> =>
    Object.entries(catalogByFlight).reduce((acc, [flightId, items]) => {
      const allRelevantPaxes = Array.from(new Set(items.flatMap((i) => Object.keys(i.services[flightId]))));
      const byPax = allRelevantPaxes.reduce((byPaxAcc, paxId) => {
        const allItemsForPaxForFlight = items.flatMap((i) => i.services?.[flightId]?.[paxId] || []);
        return {
          ...byPaxAcc,
          [paxId]: allItemsForPaxForFlight,
        };
      }, {});
      return {
        ...acc,
        [flightId]: byPax,
      };
    }, {})
);

/**
 * Returns all available services for a flight+passenger+category.
 */
export const bundleCatalogServicesForFlightAndPassengerInCategorySelector = (props: {
  flightId: string;
  travelerId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, FinnairServiceVariantItemV3[]> =>
  createSelector(
    seatBundleCatalogServicesByFlightAndPassengerSelector,
    seatBundleSubCategoriesToCategoriesMappingSelector,
    (seatBundlesByFlightAndPax, subCategoryMapping) =>
      seatBundlesByFlightAndPax[props.flightId]?.[props.travelerId]?.filter(
        (s) => subCategoryMapping[s.subCategory] === props.category
      ) || []
  );

/**
 * Returns whether the catalog offers a bundle that matches flight+passenger+category.
 */
export const seatBundleCatalogContainsMatchSelector = (props: {
  flightId: string;
  travelerId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, boolean> =>
  createSelector(
    bundleCatalogServicesForFlightAndPassengerInCategorySelector(props),
    (matchingServices) => matchingServices.length > 0
  );

/**
 * Returns subset of services selections that matches any of the seat bundle categories.
 * Maps missing selections to empty objects to reduce need for undefined checking everywhere.
 */
export const seatBundleSelectionsSelector = createSelector(
  selectCommonBookingState,
  (state): Record<string, Partial<ServiceSelections>> =>
    SEAT_BUNDLE_CATEGORIES.reduce(
      (selections, category) => ({
        ...selections,
        [category]: state?.services?.selections?.[category as keyof ServiceSelections] || {},
      }),
      {}
    )
);

/**
 * Returns mapping of flights to passengers to seat bundle services they have selected
 */
export const seatBundleSelectionsByFlightAndPassengerSelector = createSelector(
  seatBundleSelectionsSelector,
  (allSeatBundleSelections): Record<string, Record<string, TravelerService[]>> => {
    const flightIds = new Set(Object.values(allSeatBundleSelections).flatMap((s) => Object.keys(s)));
    const paxIds = new Set(
      Object.values(allSeatBundleSelections).flatMap((s) => Object.values(s).flatMap((p) => Object.keys(p)))
    );
    return Array.from(flightIds).reduce(
      (byFlightAcc, curFlightId) => ({
        ...byFlightAcc,
        [curFlightId]: Array.from(paxIds).reduce(
          (byPaxAcc, curPaxId) => ({
            ...byPaxAcc,
            [curPaxId]: Object.values(allSeatBundleSelections).flatMap((s) => s[curFlightId]?.[curPaxId] || []),
          }),
          {}
        ),
      }),
      {}
    );
  }
);

/**
 * Returns whether the passenger has an active bundle of a specific category for the given flight.
 */
export const passengerHasSpecificBundleSelector = (props: {
  flightId: string;
  travelerId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, boolean | undefined> =>
  createSelector(
    seatBundleSelectionsByFlightAndPassengerSelector,
    seatBundleSubCategoriesToCategoriesMappingSelector,
    (selections, subCategoryMapping): boolean | undefined =>
      selections[props.flightId]?.[props.travelerId]?.some(
        (s: TravelerService) => subCategoryMapping[s.category] === props.category
      )
  );

/**
 * Returns the minimum quota value across all service variants in a given bundle category for a flight.
 * Returns undefined if no quota is set on any of the variants.
 */
export const bundleQuotaForFlightAndCategorySelector = (props: {
  flightId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, number | undefined> =>
  createSelector(
    seatBundleCatalogServicesByFlightAndPassengerSelector,
    seatBundleCategoriesToSubCategoriesMappingSelector,
    (catalogByFlight, categoryMapping): number | undefined => {
      const subCatsForCategory = categoryMapping[props.category];
      if (!subCatsForCategory) {
        return undefined;
      }
      const quotas = Object.values(catalogByFlight[props.flightId] || {})
        .flat()
        .filter((serviceVariant) => subCatsForCategory.has(serviceVariant.subCategory))
        .map((serviceVariant) => serviceVariant.quota)
        .filter((quotaValue): quotaValue is number => quotaValue !== undefined && quotaValue !== null);
      return quotas.length ? Math.min(...quotas) : undefined;
    }
  );

/**
 * Returns how many passengers have already selected a bundle of the given category for the given flight.
 */
export const bundleSelectionsCountForFlightAndCategorySelector = (props: {
  flightId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, number> =>
  createSelector(
    seatBundleSelectionsByFlightAndPassengerSelector,
    seatBundleSubCategoriesToCategoriesMappingSelector,
    (selectionsByFlightAndPax, subCategoryMapping): number => {
      const passengerSelections = selectionsByFlightAndPax[props.flightId] || {};
      return Object.values(passengerSelections).filter((paxServices) =>
        paxServices.some((service) => subCategoryMapping[service.category] === props.category)
      ).length;
    }
  );

/**
 * Returns true when the available quota for a bundle category on a flight has been fully consumed by
 * the current passenger selections (i.e., no more passengers can add this bundle).
 */
export const bundleQuotaReachedForFlightAndCategorySelector = (props: {
  flightId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, boolean> =>
  createSelector(
    bundleQuotaForFlightAndCategorySelector(props),
    bundleSelectionsCountForFlightAndCategorySelector(props),
    (quota, selectedCount): boolean => quota !== undefined && selectedCount >= quota
  );

/**
 * Returns the lowest price for a specific bundle category and flight from the seat bundle catalog.
 * Returns null if no catalog item exists for the given category/flight or if no lowestPrice is set.
 */
export const seatBundleLowestPriceForFlightAndCategorySelector = (
  flightId: string,
  category: Category
): MemoizedSelector<CommonBookingFeatureState, FinnairLowestPrice | null> =>
  createSelector(seatBundleCatalogByFlightSelector, (catalogByFlight): FinnairLowestPrice | null => {
    const catalogForFlight = catalogByFlight[flightId] || [];
    const catalogForCategory = catalogForFlight.find((item) => item.category === category);
    if (!catalogForCategory?.lowestPrice) {
      return null;
    }
    return getLowestPrice(catalogForCategory.lowestPrice, [flightId]);
  });

/**
 * Returns whether the passenger should be able to add bundle to a flight (is in catalog, doesn't yet have conflicting bundle, and quota not exhausted).
 */
export const passengerCanAddBundleSelector = (props: {
  flightId: string;
  travelerId: string;
  category: Category;
}): MemoizedSelector<CommonBookingFeatureState, boolean> =>
  createSelector(
    seatBundleCatalogContainsMatchSelector(props),
    seatBundleSelectionsByFlightAndPassengerSelector,
    seatBundleSubCategoriesToCategoriesMappingSelector,
    bundleQuotaReachedForFlightAndCategorySelector({ flightId: props.flightId, category: props.category }),
    (availableInCatalog, selectedBundlesByFlightAndPax, subCategoryMapping, quotaReached) =>
      availableInCatalog &&
      !selectedBundlesByFlightAndPax[props.flightId]?.[props.travelerId]?.some(
        (service) => subCategoryMapping[service.category] === props.category
      ) &&
      !quotaReached
  );

/** flightId => travelerId => bundle category => seat number => the bundle service offered for that seat. */
export type BundleServicesBySeat = Record<
  string,
  Record<string, Record<string, Record<string, FinnairServiceVariantItemV3>>>
>;

/**
 * Returns a map of flights to passengers to seat bundle categories to seat numbers to the specific matching service.
 * E.g. returnedValue[flightId][paxId][Category.SEAT_WIFI]['7B'] will return the matching traveler service if any.
 */
export const bundleServiceMatchesForSeatSelector: MemoizedSelector<CommonBookingFeatureState, BundleServicesBySeat> =
  createSelector(
    seatBundleCatalogServicesByFlightAndPassengerSelector,
    seatBundleSubCategoriesToCategoriesMappingSelector,
    (byFlight, subCatsToCats): BundleServicesBySeat =>
      mapValues(byFlight, (byPax) =>
        mapValues(byPax, (services) =>
          services.reduce<Record<string, Record<string, FinnairServiceVariantItemV3>>>((acc, cur) => {
            const category = subCatsToCats[cur.subCategory];
            return {
              ...acc,
              [category]: {
                ...(acc[category] || {}),
                ...(cur.parameters?.seatNumbers || []).reduce(
                  (snAcc, snCur) => ({
                    ...snAcc,
                    [snCur]: cur,
                  }),
                  {} as Record<string, FinnairServiceVariantItemV3>
                ),
              },
            };
          }, {})
        )
      )
  );

/**
 * Returns passengers mapped to the top level bundle categories that they have active for a given flight.
 */
export const selectedBundleCategoriesByPassengerSelector = (props: {
  flightId: string;
}): MemoizedSelector<CommonBookingFeatureState, Record<string, Category[]>> =>
  createSelector(
    seatBundleCategoriesToSubCategoriesMappingSelector,
    seatBundleSelectionsByFlightAndPassengerSelector,
    (bundleCategoriesMapping, selectionsByFlightAndPax): Record<string, Category[]> => {
      const mappingEntries = Object.entries(bundleCategoriesMapping) as Array<[Category, Set<SubCategory>]>;
      return mapValues(selectionsByFlightAndPax[props.flightId] || {}, (bundleServicesSelected) =>
        bundleServicesSelected.map((s) => mappingEntries.find(([_cat, subCats]) => subCats.has(s.category))[0])
      );
    }
  );

/**
 * What the catalog offers for every bundle category, seat bundles included: the bundle's own item per
 * passenger, its declared constituent variants, and the standalone services it is composed of. Seat bundles
 * ship one catalog item per seat type, so `bundleItemByPax` holds the first of them; consumers that need the
 * per-seat item read `bundleServiceMatchesForSeatSelector` instead.
 */
export const bundlesByCategorySelector: MemoizedSelector<CommonBookingFeatureState, BundlesByCategory> = createSelector(
  serviceCategoriesMapSelector,
  (categoriesMap): BundlesByCategory =>
    (Object.keys(categoriesMap ?? {}) as Category[]).filter(isBundle).reduce((bundlesByCategory, bundleCategory) => {
      const constituentCategories = getRelatedCategoriesForCategory(bundleCategory).filter(
        (category) => category !== Category.SEAT
      );
      const bundlesByFragment = Object.entries(categoriesMap[bundleCategory] ?? {}).reduce(
        (bundlesByFragment, [fragmentId, bundleItemsByPax]) => {
          const segmentId = asSegmentId(fragmentId);
          const bundleItem = Object.values(bundleItemsByPax ?? {}).flat()[0];
          const variantCodes = getBundleVariantCodes(bundleItem?.variant);
          const constituentVariants = isSeatBundle(bundleCategory) ? variantCodes.slice(-1) : variantCodes;

          bundlesByFragment[segmentId] = {
            constituentVariants,
            constituents: constituentCategories
              .map((constituentCategory) =>
                resolveConstituentService(categoriesMap[constituentCategory], segmentId, constituentVariants)
              )
              .filter((service): service is ServiceCatalogService => !!service),
            bundleItemByPax: mapValues(bundleItemsByPax ?? {}, (bundleItems) => bundleItems?.[0]),
          };

          return bundlesByFragment;
        },
        {} as Record<string, BundleForFragment>
      );

      return { ...bundlesByCategory, [bundleCategory]: bundlesByFragment };
    }, {} as BundlesByCategory)
);

/**
 * Resolves the standalone ancillary service to display in the product-info modal for a given seat bundle
 * category (e.g. SEAT_MEAL → the MEAL service matching the bundle's variant). Seat bundles borrow their
 * modal content from the constituent ancillary's standalone catalog entry.
 */
export const bundleProductInfoSelector = (
  bundleCategory: Category | null
): MemoizedSelector<CommonBookingFeatureState, ServiceCatalogService | null> =>
  createSelector(
    serviceCategoriesMapSelector,
    bundlesByCategorySelector,
    (categoriesMap, bundleCatalog): ServiceCatalogService | null => {
      const ancillaryCategory = bundleCategory ? (BUNDLE_CATEGORY_ANCILLARY_MAP[bundleCategory] ?? null) : null;
      if (!ancillaryCategory) {
        return null;
      }
      const bundledService = Object.values(bundleCatalog[bundleCategory] ?? {})
        .flatMap((fragment) => fragment.constituents)
        .find((service) => service.category === ancillaryCategory);
      if (bundledService) {
        return bundledService;
      }
      return (
        Object.values(categoriesMap?.[ancillaryCategory] ?? {})
          .flatMap((perFragment) => Object.values(perFragment))
          .flat()[0] ?? null
      );
    }
  );
